import React, { useState, useEffect } from "react";
import {
  BarChart3,
  TrendingUp,
  Shield,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Info,
  Filter,
  Download,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Eye,
  AlertCircle,
  Target,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { getResults } from "./store/actions/riskTableActions";
import { fetchKybDetailRequest } from "./store/actions/kybActions";
import { BeatLoader } from "react-spinners";
import warning from "./images/warning.png";
import axios from "axios";
import { DGTL_BACKEND_URL } from "./store/URL";

// Custom CSS styles
const styles = `
  .dashboard-container {
    min-height: 100vh;
    background: linear-gradient(135deg, #f8fafc 0%, #e0f2fe 50%, #e8eaf6 100%);
    padding: 1.5rem;
  }

  .max-width-container {
    max-width: 1280px;
    margin: 0 auto;
  }

  .header-section {
    margin-bottom: 2rem;
  }

  .header-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 1.5rem;
  }

  .header-title {
    font-size: 1.575rem;
    font-weight: 700;
    color: #111827;
    margin-bottom: 0.5rem;
  }

  .header-subtitle {
    color: #6b7280;
  }

  .button-group {
    display: flex;
    gap: 0.75rem;
  }

  .btn {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    border-radius: 0.5rem;
    font-weight: 500;
    transition: all 0.2s;
    cursor: pointer;
    border: none;
    font-size: 0.875rem;
  }

  .btn-secondary {
    background: white;
    border: 1px solid #e5e7eb;
    color: #374151;
  }

  .btn-secondary:hover {
    background: #f9fafb;
  }

  .btn-primary {
    background: #2563eb;
    color: white;
  }

  .btn-primary:hover {
    background: #1d4ed8;
  }

  .cards-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 1.5rem;
    margin-bottom: 2rem;
  }

  .risk-card {
    background: white;
    border-radius: 0.75rem;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
    padding: 1.5rem;
    border: 1px solid #f3f4f6;
  }

  .card-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .card-text {
    font-size: 0.875rem;
    font-weight: 500;
    color: #6b7280;
  }

  .card-value {
    font-size: 1.875rem;
    font-weight: 700;
    color: #111827;
    margin-top: 0.25rem;
  }

  .card-value-medium {
    font-size: 1.5rem;
    font-weight: 700;
    color: #111827;
    margin-top: 0.25rem;
  }

  .card-icon {
    padding: 0.75rem;
    border-radius: 0.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .icon-orange {
    background: #fed7aa;
  }

  .icon-red {
    background: #fecaca;
  }

  .icon-yellow {
    background: #fef3c7;
  }

  .icon-blue {
    background: #dbeafe;
  }

  .card-badge {
    margin-top: 1rem;
  }

  .badge {
    display: inline-flex;
    align-items: center;
    padding: 0.25rem 0.75rem;
    border-radius: 9999px;
    font-size: 0.875rem;
    font-weight: 500;
  }

  .badge-orange {
    background: #fed7aa;
    color: #9a3412;
  }

  .tabs-container {
    margin-bottom: 2rem;
  }

  .tabs-wrapper {
    background: white;
    border-radius: 0.5rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    border: 1px solid #e5e7eb;
    padding: 0.5rem;
  }

  .tabs-list {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .tab-button {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    border-radius: 0.5rem;
    font-weight: 500;
    transition: all 0.2s;
    cursor: pointer;
    border: none;
    background: none;
  }

  .tab-active {
    background: #2563eb;
    color: white;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  }

  .tab-inactive {
    color: #6b7280;
  }

  .tab-inactive:hover {
    background: #f3f4f6;
  }

  .filters-section {
    margin-bottom: 1.5rem;
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .filter-label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .filter-text {
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
  }

  .filter-select {
    padding: 0.5rem 0.75rem;
    border: 1px solid #d1d5db;
    border-radius: 0.5rem;
    background: white;
    color: #111827;
  }

  .filter-select:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
  }

  .main-table-container {
    background: white;
    border-radius: 0.75rem;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
    border: 1px solid #e5e7eb;
    overflow: hidden;
  }

  .table-header {
    padding: 1.5rem;
    border-bottom: 1px solid #e5e7eb;
  }

  .table-title {
    font-size: 1.25rem;
    font-weight: 600;
    color: #111827;
  }

  .table-subtitle {
    color: #6b7280;
    margin-top: 0.25rem;
  }

  .table-wrapper {
    overflow-x: auto;
  }

  .risk-table {
    width: 100%;
    border-collapse: collapse;
  }

  .table-head {
    background: #f9fafb;
  }

  .table-head th {
    padding: 1rem 1.5rem;
    text-align: left;
    font-size: 0.75rem;
    font-weight: 500;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .table-head th.text-center {
    text-align: center;
  }

  .table-body {
    background: white;
  }

  .table-row {
    border-bottom: 1px solid #e5e7eb;
    transition: background-color 0.2s;
  }

  .table-row:hover {
    background: #f9fafb;
  }

  .table-cell {
    padding: 1rem 1.5rem;
  }

  .table-cell.text-center {
    text-align: center;
  }

  .cell-content {
    display: flex;
    align-items: center;
  }

  .cell-icon {
    margin-right: 0.75rem;
    flex-shrink: 0;
  }

  .cell-text-primary {
    font-size: 0.875rem;
    font-weight: 500;
    color: #111827;
  }

  .cell-text-secondary {
    font-size: 0.75rem;
    color: #6b7280;
  }

  .risk-badge {
    display: inline-flex;
    align-items: center;
    padding: 0.125rem 0.625rem;
    border-radius: 9999px;
    font-size: 0.75rem;
    font-weight: 500;
  }

  .risk-emerald {
    color: #059669;
    background: #ecfdf5;
  }

  .risk-yellow {
    color: #d97706;
    background: #fffbeb;
  }

  .risk-orange {
    color: #ea580c;
    background: #fff7ed;
  }

  .risk-red {
    color: #dc2626;
    background: #fef2f2;
  }

  .weight-display {
    font-size: 0.875rem;
    color: #111827;
  }

  .progress-bar {
    width: 4rem;
    height: 0.5rem;
    background: #e5e7eb;
    border-radius: 9999px;
    margin: 0 auto;
    margin-top: 0.25rem;
    overflow: hidden;
  }

  .progress-fill {
    height: 100%;
    background: #2563eb;
    border-radius: 9999px;
  }

  .score-text {
    font-size: 0.875rem;
    font-weight: 600;
    color: #111827;
  }

  .value-text {
    font-size: 0.875rem;
    color: #111827;
  }

  .source-badge {
    display: inline-flex;
    align-items: center;
    padding: 0.125rem 0.625rem;
    border-radius: 9999px;
    font-size: 0.75rem;
    font-weight: 500;
    background: #dbeafe;
    color: #1e40af;
  }

  .expand-button {
    color: #2563eb;
    background: none;
    border: none;
    cursor: pointer;
    transition: color 0.2s;
  }

  .expand-button:hover {
    color: #1d4ed8;
  }

  .expanded-row {
    background: #f9fafb;
  }

  .expanded-content {
    padding: 1rem 1.5rem;
  }

  .expanded-inner {
    background: white;
    border-radius: 0.5rem;
    padding: 1rem;
    border: 1px solid #e5e7eb;
  }

  .expanded-title {
    font-size: 0.875rem;
    font-weight: 500;
    color: #111827;
    margin-bottom: 0.5rem;
  }

  .expanded-text {
    font-size: 0.875rem;
    color: #374151;
  }

  .summary-footer {
    background: #f9fafb;
    padding: 1.5rem;
    border-top: 1px solid #e5e7eb;
  }

  .summary-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
  }

  .summary-card {
    background: white;
    border-radius: 0.5rem;
    padding: 1rem;
    border: 1px solid #e5e7eb;
  }

  .summary-label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #6b7280;
  }

  .summary-value {
    font-size: 1.5rem;
    font-weight: 700;
    color: #111827;
    margin-top: 0.25rem;
  }

  .summary-value-orange {
    font-size: 1.5rem;
    font-weight: 700;
    color: #ea580c;
    margin-top: 0.25rem;
  }

  .summary-classification {
    font-size: 1.125rem;
    font-weight: 600;
    color: #ea580c;
    margin-top: 0.25rem;
  }

  .matrix-table {
    margin-top: 2rem;
    background: white;
    border-radius: 0.75rem;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
    border: 1px solid #e5e7eb;
    overflow: hidden;
  }

  .highlighted-row {
    background: #fff7ed;
  }

  .status-dot {
    width: 1rem;
    height: 1rem;
    border-radius: 50%;
    margin: 0 auto;
  }

  .dot-emerald {
    background: #10b981;
  }

  .dot-yellow {
    background: #f59e0b;
  }

  .dot-orange {
    background: #f97316;
  }

  .dot-red {
    background: #ef4444;
  }

  .dot-purple {
    background: #8b5cf6;
  }

  .icon-color-orange {
    color: #ea580c;
  }

  .icon-color-red {
    color: #dc2626;
  }

  .icon-color-yellow {
    color: #d97706;
  }

  .icon-color-blue {
    color: #2563eb;
  }

  .icon-color-emerald {
    color: #059669;
  }

  .icon-color-gray {
    color: #6b7280;
  }

  .loading-container {
    height: 500px;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .analysis-box {
    background: white;
    border-radius: 0.75rem;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
    padding: 2rem;
    border: 1px solid #e5e7eb;
  }

  .analysis-report-heading {
    font-size: 1.5rem;
    font-weight: 600;
    color: #111827;
    margin-bottom: 1rem;
  }

  .analysis-report-message {
    color: #374151;
    line-height: 1.6;
  }

  .analysis-risk-items {
    margin: 0.5rem 0;
    color: #374151;
  }

  .navigation-links {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
  }

  .link-back {
    color: #2563eb;
    cursor: pointer;
    text-decoration: underline;
  }

  .link-back:hover {
    color: #1d4ed8;
  }

  @media (max-width: 768px) {
    .dashboard-container {
      padding: 1rem;
    }

    .header-content {
      flex-direction: column;
      gap: 1rem;
      align-items: flex-start;
    }

    .cards-grid {
      grid-template-columns: 1fr;
    }

    .tabs-list {
      flex-direction: column;
    }

    .summary-grid {
      grid-template-columns: 1fr;
    }

    .filters-section {
      flex-direction: column;
      align-items: flex-start;
    }

    .table-wrapper {
      overflow-x: scroll;
    }
  }

  @media (max-width: 480px) {
    .header-title {
      font-size: 1.5rem;
    }

    .card-value {
      font-size: 1.5rem;
    }

    .card-value-medium {
      font-size: 1.25rem;
    }

    .risk-card {
      padding: 1rem;
    }

    .table-header {
      padding: 1rem;
    }

    .table-cell {
      padding: 0.75rem 1rem;
    }

    .summary-footer {
      padding: 1rem;
    }
  }
`;

const KYBRiskTable = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState("Show All");
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [filterRisk, setFilterRisk] = useState("all");
  const [riskArr, setNewriskArr] = useState([]);
  const [riskCategory, setRiskCategory] = useState({});
  const { user, company } = useSelector((state) => state.user);

  // Redux selectors
  const apiData = useSelector((state) => state.riskTable);
  const userInfo = useSelector((state) => state.user);
  // const { kybDetail } = useSelector((state) => state.kyb);
  const [kybDetail, setKybDetail] = useState(null);

  // Get data from localStorage
  const kybId =
    typeof window !== "undefined" ? localStorage.getItem("kybID") : null;
  const token =
    typeof window !== "undefined" ? localStorage.getItem("token") : null;

  // Fetch data on component mount
  useEffect(() => {
    if (!apiData?.riskTableData && token && !kybDetail) {
      // dispatch(fetchKybDetailRequest(kybId));
      fetchKybDetailRequest();
      dispatch(getResults(kybId, "corporate"));
    }
  }, [dispatch, apiData, kybId, token, kybDetail]);

  async function fetchKybDetailRequest() {
    const token = localStorage.getItem("token");
    const kybId = localStorage.getItem("kybID");

    let body = {
      id: kybId, // Use kycId in the request body
      type: "Entity",
    };

    const response = await axios.post(
      `${DGTL_BACKEND_URL}/admin/kyb/getCorporateClientDetails`,
      body,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    const { data } = response.data;
    setKybDetail(data);
    console.log("KYB Data", data);
  }

  // Helper function to get highest score object
  function getHighestScoreObject(array) {
    if (!array || array.length === 0) return null;
    return array.reduce((max, current) => {
      return current.score > max.score ? current : max;
    }, array[0]);
  }
  useEffect(() => {
    if (token && !company && !user) {
      dispatch({ type: "LOAD_USER_REQUEST" }); // Trigger the saga to fetch user data
    } else {
      if (user?.accountType !== "admin" && user?.accountType !== "approver") {
        navigate("/forbidden");
      }
    }
  }, [dispatch, company, user]);

  // Process API data when it changes
  useEffect(() => {
    if (apiData?.riskTableData) {
      const adjustedData = apiData.riskTableData.detailedResults.map((e) => {
        return {
          calculatedRisk: e.calculatedRisk,
          name: e.name,
          risk: e.risk,
          title: e.title,
          riskLevel: Number(e.riskLevel),
          weight: Number(e.weight),
          riskrating: Number(e.riskLevel) * Number(e.weight),
          Sample: "kyc data",
          DataSource: e.DataSource,
          datasrc: null,
        };
      });

      setNewriskArr(adjustedData);

      const riskObject = {
        impact: apiData.riskTableData.impact,
        overallRisk: apiData.riskTableData.overallRisk,
        probability: apiData.riskTableData.probability,
        riskCategory: apiData.riskTableData.riskCategory,
        totalRisk: apiData.riskTableData.totalRisk,
      };

      setRiskCategory(riskObject);
    }
  }, [apiData]);

  const brandingStyles = `
  .company-branding {
    background: white;
    border-radius: 0.75rem;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    padding: 1.5rem;
    margin-bottom: 2rem;
    border: 1px solid #e5e7eb;
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .company-logo {
    width: 4rem;
    height: 4rem;
    border-radius: 0.5rem;
    object-fit: contain;
    border: 1px solid #e5e7eb;
    background: #f9fafb;
    padding: 0.5rem;
  }

  .company-info {
    flex: 1;
  }

  .company-name {
    font-size: 1.5rem;
    font-weight: 700;
    color: #111827;
    margin: 0;
  }

  .company-subtitle {
    font-size: 0.875rem;
    color: #6b7280;
    margin-top: 0.25rem;
  }

  @media (max-width: 768px) {
    .company-branding {
      flex-direction: column;
      text-align: center;
    }
    
    .company-logo {
      width: 3rem;
      height: 3rem;
    }
    
    .company-name {
      font-size: 1.25rem;
    }
  }
`;

  const CompanyBranding = () => (
    <div className="d-flex flex-column align-items-center justify-content-center mb-5">
      {company?.image && (
        <img
          src={company.image}
          alt={`${company.companyName || "Company"} Logo`}
          style={{
            width: "120px",
            height: "120px",
          }}
          onError={(e) => {
            e.target.style.display = "none";
          }}
        />
      )}
      <div className="d-flex text-center flex-column ms-3 company-branding">
        <span
          style={{
            fontSize: "40px",
            fontWeight: 600,
          }}
          className="company-name"
        >
          {company?.companyName || "Company Name"}
        </span>
        <span
          style={{
            fontSize: "16px",
            fontWeight: 400,
          }}
          className="company-subtitle"
        >
          Risk Assessment Report
        </span>
      </div>
    </div>
  );

  console.log("company", company);
  // Check KYB values based on title
  function checkKYCValue(title) {
    if (!kybDetail) return "N/A";

    switch (title) {
      case "Country of Operation":
        return kybDetail.countryOfIncorporation || "N/A";
      case "Country of Residence":
        return kybDetail.taxResidences?.[0]?.country || "N/A";
      case "Other Source of Income":
        const sourceOfIncomeObject = getHighestScoreObject(
          kybDetail.OtherSourcesOfIncome
        );
        return sourceOfIncomeObject?.title || "N/A";
      case "Other Sources of Wealth":
        const sourceOfWealthObject = getHighestScoreObject(
          kybDetail.OtherSourcesOfIncome
        );
        return sourceOfWealthObject?.title || "N/A";
      case "Source of Income from Industry":
        return kybDetail.SourceOfIncomeLegalStructure?.title || "N/A";
      case "PEP Status":
        return kybDetail.principals?.[0]?.pep ? "Yes" : "No";
      case "Country of Jurisdiction":
        return kybDetail.countryOfOperation[0] || "N/A";
      case "Products Services":
        return kybDetail.services?.products?.[0] || "N/A";
      case "Risk Level for Identity":
        return kybDetail.idType1 || "N/A";
      case "Risk Level for ID Verification":
        return kybDetail.idType1 || "N/A";
      case "Anticipated Incoming activity/mth":
        return kybDetail.expectedInflows || "N/A";
      case "Anticipated Outgoing activity/mth":
        return kybDetail.expectedOutflows || "N/A";
      case "Risk Level for Proof of Address":
        return kybDetail.proofOfAddressType || "N/A";
      case "Risk Level for Source of Income Proof":
        return kybDetail.proofOfIncomeType || "N/A";
      default:
        return "N/A";
    }
  }

  // Check data source
  function checkDataSource(title, dataSource) {
    const kybSources = [
      "Country of Nationality",
      "Country of Operation",
      "Country of Residence",
      "Source of Income Legal Structure",
      "Other Source of Income",
      "Source of Income from Industry",
      "Risk Level for Identity",
      "Risk Level for ID Verification",
      "PEP Status",
      "Products Services",
      "Anticipated Incoming activity/mth",
      "Anticipated Outgoing activity/mth",
      "Risk Level for Proof of Address",
      "Risk Level for Source of Income Proof",
      "Geographic Location",
    ];

    return kybSources.includes(title) ? "KYB" : dataSource;
  }

  // Clean HTML message
  function cleanMessage(message) {
    return message ? message.replace(/^```html\s*\n|\s*```$/g, "") : "";
  }

  // Handle tab changes
  const HandleTabs = (val) => {
    setCurrentTab(val);

    if (val === "Analysis" || val === "Risk Mitigation") return;

    if (val === "Risks Identified") {
      const riskgreater = riskArr && riskArr.filter((e) => e.riskLevel >= 3);
      setNewriskArr(riskgreater);
    } else if (val === "Impact") {
      return;
    } else {
      if (apiData?.riskTableData) {
        const adjustedData = apiData.riskTableData.detailedResults.map((e) => {
          return {
            calculatedRisk: e.calculatedRisk,
            name: e.name,
            risk: e.risk,
            title: e.title,
            riskLevel: Number(e.riskLevel),
            weight: Number(e.weight),
            riskrating: Number(e.riskLevel) * Number(e.weight),
            Sample: "kyc data",
            DataSource: e.DataSource,
            datasrc: null,
          };
        });
        setNewriskArr(adjustedData);
      }
    }
  };

  const tabs = [
    { id: "Show All", label: "Complete Analysis", icon: BarChart3 },
    { id: "Risk Profile", label: "Risk Profile", icon: Target },
    { id: "Risks Identified", label: "Risk Factors", icon: AlertTriangle },
    { id: "Risk Score", label: "Risk Scoring", icon: TrendingUp },
    { id: "Analysis", label: "AI Analysis", icon: Info },
    { id: "Risk Mitigation", label: "Mitigation", icon: Shield },
  ];

  const getRiskColorClass = (level) => {
    if (level <= 2) return "risk-emerald";
    if (level <= 3) return "risk-yellow";
    if (level <= 4) return "risk-orange";
    return "risk-red";
  };

  const getRiskIcon = (level) => {
    if (level <= 2)
      return (
        <CheckCircle
          className="icon-color-emerald"
          style={{ width: "1rem", height: "1rem" }}
        />
      );
    if (level <= 3)
      return (
        <Info
          className="icon-color-yellow"
          style={{ width: "1rem", height: "1rem" }}
        />
      );
    if (level <= 4)
      return (
        <AlertTriangle
          className="icon-color-orange"
          style={{ width: "1rem", height: "1rem" }}
        />
      );
    return (
      <XCircle
        className="icon-color-red"
        style={{ width: "1rem", height: "1rem" }}
      />
    );
  };

  const toggleRowExpansion = (index) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedRows(newExpanded);
  };

  const filteredData = riskArr.filter((item) => {
    if (filterRisk === "high") return item.riskLevel >= 4;
    if (filterRisk === "medium")
      return item.riskLevel >= 2 && item.riskLevel < 4;
    if (filterRisk === "low") return item.riskLevel < 2;
    return true;
  });

  const totalRiskScore = riskArr.reduce(
    (sum, item) => sum + (item.riskrating || 0),
    0
  );
  const adjustedRiskScore = totalRiskScore * 5;

  // Risk categories table
  const riskCategoryTable = [
    {
      category: "Insignificant",
      score:
        riskCategory.riskCategory === "Insignificant"
          ? riskCategory.overallRisk?.toFixed(2)
          : "-",
      colorCode: "green",
      minScore: 5,
      maxScore: 8,
    },
    {
      category: "Minor",
      score:
        riskCategory.riskCategory === "Minor"
          ? riskCategory.overallRisk?.toFixed(2)
          : "-",
      colorCode: "yellow",
      minScore: 8.1,
      maxScore: 10,
    },
    {
      category: "Significant",
      score:
        riskCategory.riskCategory === "Significant"
          ? riskCategory.overallRisk?.toFixed(2)
          : "-",
      colorCode: "orange",
      minScore: 10.1,
      maxScore: 15,
    },
    {
      category: "Critical",
      score:
        riskCategory.riskCategory === "Critical"
          ? riskCategory.overallRisk?.toFixed(2)
          : "-",
      colorCode: "red",
      minScore: 15.1,
      maxScore: 20,
    },
    {
      category: "Catastrophic",
      score:
        riskCategory.riskCategory === "Catastrophic"
          ? riskCategory.overallRisk?.toFixed(2)
          : "-",
      colorCode: "black",
      minScore: 20.1,
      maxScore: 25,
    },
  ];

  // Show loading if data is not available
  if (!kybDetail || !apiData?.riskTableData) {
    return (
      <div>
        <style>{styles}</style>
        <div className="loading-container">
          <BeatLoader color="#0E8DEE" />
        </div>
      </div>
    );
  }

  return (
    <div>
      <style>{styles}</style>
      <div className="dashboard-container">
        <div className="max-width-container">
          {/* Header Section */}

          <div className="header-section">
            <CompanyBranding />

            <div className="header-content">
              <div>
                <h1 className="header-title">Risk Assessment Dashboard</h1>
                <p className="header-subtitle">
                  Comprehensive risk analysis for{" "}
                  {kybDetail?.fullName || "Corporate Entity"}
                </p>
              </div>
              <div className="button-group">
                <button className="btn btn-secondary">
                  <Download style={{ width: "1rem", height: "1rem" }} />
                  Export
                </button>
                <button
                  className="btn btn-primary"
                  onClick={() => dispatch(getResults(kybId, "corporate"))}
                >
                  <RefreshCw style={{ width: "1rem", height: "1rem" }} />
                  Refresh
                </button>
              </div>
            </div>

            {/* Navigation Links */}
            <div className="navigation-links">
              {userInfo?.user?.accountType === "admin" ? (
                <span
                  className="link-back"
                  onClick={() => navigate("/dashboard")}
                >
                  ← Risk Engine Settings
                </span>
              ) : (
                <div></div>
              )}
              <span
                className="link-back"
                onClick={() => navigate("/corporate-details")}
              >
                Re-evaluate User
              </span>
            </div>

            {/* Risk Score Overview Cards */}
            <div className="cards-grid">
              <div className="risk-card">
                <div className="card-content">
                  <div>
                    <p className="card-text">Impact Level</p>
                    <p className="card-value-medium">
                      {riskCategory.impact || "N/A"}
                    </p>
                  </div>
                  <div className="card-icon icon-red">
                    <AlertTriangle
                      className="icon-color-red"
                      style={{ width: "1.5rem", height: "1.5rem" }}
                    />
                  </div>
                </div>
              </div>

              <div className="risk-card">
                <div className="card-content">
                  <div>
                    <p className="card-text">Probability</p>
                    <p className="card-value-medium">
                      {riskCategory.probability || "N/A"}
                    </p>
                  </div>
                  <div className="card-icon icon-yellow">
                    <TrendingUp
                      className="icon-color-yellow"
                      style={{ width: "1.5rem", height: "1.5rem" }}
                    />
                  </div>
                </div>
              </div>

              <div className="risk-card">
                <div className="card-content">
                  <div>
                    <p className="card-text">Risk score</p>
                    <p className="card-value">
                      {totalRiskScore.toFixed(2) || "N/A"}
                      {" - "}
                      {totalRiskScore.toFixed(2) <= 2
                        ? "Low"
                        : totalRiskScore.toFixed(2) <= 3.5
                        ? "Medium"
                        : "High" || "N/A"}
                    </p>
                  </div>
                  <div className="card-icon icon-blue">
                    <Shield
                      className="icon-color-blue"
                      style={{ width: "1.5rem", height: "1.5rem" }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="tabs-container">
            <div className="tabs-wrapper">
              <div className="tabs-list">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => HandleTabs(tab.id)}
                      className={`tab-button ${
                        currentTab === tab.id ? "tab-active" : "tab-inactive"
                      }`}
                    >
                      <Icon style={{ width: "1rem", height: "1rem" }} />
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Analysis Tab Content */}
          {currentTab === "Analysis" && (
            <div className="analysis-box">
              <h3 className="analysis-report-heading">
                Analysis Report Results
              </h3>
              <span
                className="analysis-report-message"
                dangerouslySetInnerHTML={{
                  __html: cleanMessage(apiData?.riskTableData?.Analysismessage),
                }}
              ></span>
            </div>
          )}

          {/* Risk Mitigation Tab Content */}
          {currentTab === "Risk Mitigation" && (
            <div className="analysis-box">
              <h3 className="analysis-report-heading">
                Risk Mitigation Results
              </h3>
              <span className="analysis-report-message">
                The following items should be addressed in order to reduce the
                individual's risk:
              </span>
              <div className="mt-4">
                {apiData?.riskTableData?.highRiskItems?.map((item, i) => (
                  <li key={i} className="analysis-risk-items">
                    {item}
                  </li>
                ))}
              </div>
            </div>
          )}

          {/* Filters */}
          {currentTab !== "Analysis" && currentTab !== "Risk Mitigation" && (
            <div className="filters-section">
              <div className="filter-label">
                <Filter
                  className="icon-color-gray"
                  style={{ width: "1rem", height: "1rem" }}
                />
                <span className="filter-text">Filter by Risk Level:</span>
              </div>
              <select
                value={filterRisk}
                onChange={(e) => setFilterRisk(e.target.value)}
                className="filter-select"
              >
                <option value="all">All Levels</option>
                <option value="high">High Risk (4-5)</option>
                <option value="medium">Medium Risk (2-3)</option>
                <option value="low">Low Risk (1-2)</option>
              </select>
            </div>
          )}

          {/* Main Risk Table */}
          {currentTab !== "Analysis" && currentTab !== "Risk Mitigation" && (
            <div className="main-table-container">
              <div className="table-header">
                <h2 className="table-title">
                  Risk Factor Analysis -{" "}
                  {kybDetail?.fullName || "Corporate Entity"}
                </h2>
                <p className="table-subtitle">
                  Detailed breakdown of risk components and their impact
                </p>
              </div>

              <div className="table-wrapper">
                <table className="risk-table">
                  <thead className="table-head">
                    <tr>
                      <th>#</th>
                      <th>Risk Factor</th>
                      {(currentTab === "Show All" ||
                        currentTab === "Risk Profile" ||
                        currentTab === "Risks Identified") && (
                        <th className="text-center">Risk Level (1-5)</th>
                      )}
                      {(currentTab === "Show All" ||
                        currentTab === "Risk Score") && (
                        <th className="text-center">Weight (%)</th>
                      )}
                      {(currentTab === "Show All" ||
                        currentTab === "Risk Score") && (
                        <th className="text-center">Risk Rating</th>
                      )}
                      {currentTab === "Show All" && <th>Current Value</th>}
                      {(currentTab === "Show All" ||
                        currentTab === "Risks Identified") && (
                        <th>Description</th>
                      )}
                      {currentTab === "Show All" && (
                        <th className="text-center">Data Source</th>
                      )}
                      {currentTab === "Show All" && (
                        <th className="text-center">Details</th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="table-body">
                    {filteredData.map((item, index) => (
                      <React.Fragment key={index}>
                        <tr className="table-row">
                          <td className="table-cell">{index + 1}</td>
                          <td className="table-cell">
                            <div className="cell-content">
                              <div className="cell-icon">
                                {getRiskIcon(item.riskLevel)}
                              </div>
                              <div>
                                <div className="cell-text-primary">
                                  {item.name}
                                </div>
                              </div>
                            </div>
                          </td>
                          {(currentTab === "Show All" ||
                            currentTab === "Risk Profile" ||
                            currentTab === "Risks Identified") && (
                            <td className="table-cell text-center">
                              <span
                                className={`risk-badge ${getRiskColorClass(
                                  item.riskLevel
                                )}`}
                              >
                                {item.riskLevel?.toFixed(1) || "0.0"}
                              </span>
                            </td>
                          )}
                          {(currentTab === "Show All" ||
                            currentTab === "Risk Score") && (
                            <td className="table-cell text-center">
                              <div className="weight-display">
                                {item.weight}
                              </div>
                            </td>
                          )}
                          {(currentTab === "Show All" ||
                            currentTab === "Risk Score") && (
                            <td className="table-cell text-center">
                              <span className="score-text">
                                {item.riskrating?.toFixed(2) || "0.00"}
                              </span>
                            </td>
                          )}
                          {currentTab === "Show All" && (
                            <td className="table-cell">
                              <span className="value-text">
                                {checkKYCValue(item.name)}
                              </span>
                            </td>
                          )}
                          {(currentTab === "Show All" ||
                            currentTab === "Risks Identified") && (
                            <td className="table-cell">
                              <span className="value-text">
                                {item.title || "-"}
                              </span>
                            </td>
                          )}
                          {currentTab === "Show All" && (
                            <td className="table-cell text-center">
                              <div
                                className="cell-content"
                                style={{ justifyContent: "center" }}
                              >
                                <span className="source-badge">
                                  {checkDataSource(item.name, item.DataSource)}
                                </span>
                                {checkDataSource(item.name, item.DataSource) ===
                                  "Default" && (
                                  <img
                                    width={20}
                                    src={warning}
                                    alt="Warning"
                                    style={{ marginLeft: "0.5rem" }}
                                  />
                                )}
                              </div>
                            </td>
                          )}
                          {currentTab === "Show All" && (
                            <td className="table-cell text-center">
                              <button
                                onClick={() => toggleRowExpansion(index)}
                                className="expand-button"
                              >
                                {expandedRows.has(index) ? (
                                  <ChevronDown
                                    style={{ width: "1rem", height: "1rem" }}
                                  />
                                ) : (
                                  <ChevronRight
                                    style={{ width: "1rem", height: "1rem" }}
                                  />
                                )}
                              </button>
                            </td>
                          )}
                        </tr>
                        {currentTab === "Show All" &&
                          expandedRows.has(index) && (
                            <tr className="table-row expanded-row">
                              <td colSpan="8" className="expanded-content">
                                <div className="expanded-inner">
                                  <h4 className="expanded-title">
                                    Risk Factor Details
                                  </h4>
                                  <p className="expanded-text">
                                    {item.title ||
                                      "No additional details available"}
                                  </p>
                                </div>
                              </td>
                            </tr>
                          )}
                      </React.Fragment>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td></td>
                      <th>Risk Factor Score (Total)</th>
                      {(currentTab === "Show All" ||
                        currentTab === "Risk Profile" ||
                        currentTab === "Risks Identified") && (
                        <th className="text-center">
                          {filteredData
                            .reduce(
                              (sum, item) => sum + (item.riskLevel || 0),
                              0
                            )
                            .toFixed(2)}
                        </th>
                      )}
                      {(currentTab === "Show All" ||
                        currentTab === "Risk Score") && (
                        <th className="text-center">
                          {filteredData
                            .reduce((sum, item) => sum + (item.weight || 0), 0)
                            .toFixed(2)}
                        </th>
                      )}
                      {(currentTab === "Show All" ||
                        currentTab === "Risk Score") && (
                        <th className="text-center">
                          {totalRiskScore.toFixed(2)}
                        </th>
                      )}
                      {currentTab === "Show All" && <th>-</th>}
                      {currentTab === "Show All" && <th>-</th>}
                      {currentTab === "Show All" && <th>-</th>}
                    </tr>
                    <tr>
                      <th></th>
                      <th>
                        Account Holder Risk to Bank (adjustment factor x5)
                      </th>
                      <th
                        className="text-center"
                        colSpan={currentTab === "Show All" ? "6" : "4"}
                      >
                        <div>
                          <span className="score-text">
                            {adjustedRiskScore.toFixed(2)}
                          </span>
                          <div
                            style={{
                              marginTop: "0.5rem",
                              fontSize: "0.875rem",
                            }}
                          >
                            Risk Classification:{" "}
                            {adjustedRiskScore <= 33
                              ? "Low"
                              : adjustedRiskScore <= 66
                              ? "Medium"
                              : "High"}
                          </div>
                        </div>
                      </th>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}

          {/* Risk Categories Table */}
          {(currentTab === "Show All" || currentTab === "Impact") && (
            <>
              <div className="matrix-table">
                <div className="table-header">
                  <h2 className="table-title">
                    Risk Category - Impact - Probability
                  </h2>
                </div>
                <div className="table-wrapper">
                  <table className="risk-table">
                    <thead className="table-head">
                      <tr>
                        <th className="text-center">Risk Category</th>
                        <th className="text-center">Score</th>
                        <th className="text-center">Impact</th>
                        <th className="text-center">Probability</th>
                      </tr>
                    </thead>
                    <tbody className="table-body">
                      <tr className="highlighted-row">
                        <th className="text-center">
                          {riskCategory.riskCategory || "N/A"}
                        </th>
                        <td className="text-center">
                          {riskCategory.overallRisk?.toFixed(2) || "-"}
                        </td>
                        <td className="text-center">
                          {riskCategory.impact || "-"}
                        </td>
                        <td className="text-center">
                          {riskCategory.probability || "-"}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="matrix-table">
                <div className="table-header">
                  <h2 className="table-title">Risk Category Matrix</h2>
                </div>
                <div className="table-wrapper">
                  <table className="risk-table">
                    <thead className="table-head">
                      <tr>
                        <th>Risk Category</th>
                        <th className="text-center">Score</th>
                        <th className="text-center">Color Code</th>
                        <th className="text-center">Min Risk Score</th>
                        <th className="text-center">Max Risk Score</th>
                      </tr>
                    </thead>
                    <tbody className="table-body">
                      {riskCategoryTable.map((category, index) => (
                        <tr
                          key={index}
                          className={
                            category.score !== "-"
                              ? "highlighted-row"
                              : "table-row"
                          }
                        >
                          <th className="text-center">{category.category}</th>
                          <td className="text-center">{category.score}</td>
                          <td
                            className="text-center"
                            style={{
                              backgroundColor: category.colorCode,
                              color:
                                category.colorCode === "black"
                                  ? "white"
                                  : "inherit",
                            }}
                          >
                            <div
                              className={`status-dot dot-${
                                category.colorCode === "green"
                                  ? "emerald"
                                  : category.colorCode === "yellow"
                                  ? "yellow"
                                  : category.colorCode === "orange"
                                  ? "orange"
                                  : category.colorCode === "red"
                                  ? "red"
                                  : "purple"
                              }`}
                            ></div>
                          </td>
                          <td className="text-center">{category.minScore}</td>
                          <td className="text-center">{category.maxScore}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default KYBRiskTable;
